import styled, { keyframes } from 'styled-components';

/* ─── Brand tokens (shared with PaymentInvoices) ─── */
const brand = {
  blue: '#0d4efe',
  teal: '#01ebb2',
  textPrimary: '#1a2340',
  textSecondary: '#5a6478',
  textMuted: '#8a93a6',
  border: '#e0e5f0',
  inputBg: '#f7f9fc',
  white: '#ffffff',
  cardBg: '#ffffff',
  danger: '#e74c3c',
};

/* ─── Status action button tone map ─── */
const actionTones = {
  accept: '#0a6e3a',
  decline: '#b71c1c',
  complete: '#0d4efe',
};

const actionToneBg = {
  accept: 'rgba(1, 235, 178, 0.12)',
  decline: 'rgba(231, 76, 60, 0.08)',
  complete: 'rgba(13, 78, 254, 0.08)',
};

/* ─── Urgency badge color map ─── */
const urgencyColors = {
  asap: { bg: 'rgba(231, 76, 60, 0.12)', color: '#b71c1c', border: 'rgba(231, 76, 60, 0.4)' },
  'this-week': { bg: 'rgba(13, 78, 254, 0.12)', color: '#0d4efe', border: 'rgba(13, 78, 254, 0.4)' },
  flexible: { bg: 'rgba(1, 235, 178, 0.15)', color: '#0a6e3a', border: 'rgba(1, 235, 178, 0.5)' },
  'just-quotes': { bg: 'rgba(138, 147, 166, 0.15)', color: '#5a6478', border: 'rgba(138, 147, 166, 0.4)' },
};

/* ─── Service request status badge color map ─── */
const statusColors = {
  pending: { bg: 'rgba(13, 78, 254, 0.12)', color: '#0d4efe', border: 'rgba(13, 78, 254, 0.4)' },
  accepted: { bg: 'rgba(1, 235, 178, 0.15)', color: '#0a6e3a', border: 'rgba(1, 235, 178, 0.5)' },
  declined: { bg: 'rgba(231, 76, 60, 0.12)', color: '#b71c1c', border: 'rgba(231, 76, 60, 0.4)' },
  completed: { bg: 'rgba(1, 235, 178, 0.15)', color: '#0a6e3a', border: 'rgba(1, 235, 178, 0.5)' },
};

/* ─── Animations ─── */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Page wrapper ─── */
export const PageWrapper = styled.div`
  width: 100%;
  max-width: 1080px;
  margin: 2rem auto;
  padding: 2.5rem 2.5rem 2rem;
  background: ${brand.cardBg};
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(10, 22, 40, 0.08);
  animation: ${fadeIn} 0.5s ease both;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, ${brand.teal} 0%, ${brand.blue} 100%);
  }

  @media (max-width: 600px) {
    max-width: 100%;
    margin: 1rem;
    padding: 1.75rem 1.25rem 1.5rem;
  }
`;

/* ─── Header (title + subtitle) ─── */
export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

export const PageHeading = styled.h2`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.4rem;
  font-weight: 800;
  margin: 0;
  background: linear-gradient(90deg, ${brand.teal} 0%, ${brand.blue} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const SubHeading = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 0.88rem;
  color: ${brand.textMuted};
  margin: 0.35rem 0 0;
`;

export const SearchBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 1.25rem;
  padding: 0.65rem 0.85rem;
  background: ${brand.inputBg};
  border: 1px solid ${brand.border};
  border-radius: 12px;
  max-width: 420px;

  svg {
    color: ${brand.textMuted};
    font-size: 1.15rem;
    flex-shrink: 0;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-family: 'Roboto', sans-serif;
  font-size: 0.9rem;
  color: ${brand.textPrimary};
  outline: none;

  &::placeholder {
    color: ${brand.textMuted};
  }
`;

export const SearchHint = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 0.85rem;
  color: ${brand.textMuted};
  margin: -0.5rem 0 1rem;
`;

/* ─── Section heading (Open / Closed Service Requests) ─── */
export const SectionHeading = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: ${brand.textPrimary};
  margin: 2rem 0 0.85rem;

  &::before {
    content: '';
    width: 4px;
    height: 1em;
    border-radius: 2px;
    background: linear-gradient(180deg, ${brand.teal} 0%, ${brand.blue} 100%);
  }

  &:first-of-type {
    margin-top: 0;
  }
`;

/* ─── Service requests table ─── */
export const TableScroller = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid ${brand.border};
  border-radius: 12px;
`;

export const ServiceRequestTable = styled.table`
  width: 100%;
  min-width: 720px;
  table-layout: fixed;
  border-collapse: collapse;
`;

export const TableHead = styled.thead`
  background: linear-gradient(90deg, rgba(1, 235, 178, 0.08) 0%, rgba(13, 78, 254, 0.05) 100%);
`;

export const Th = styled.th`
  text-align: left;
  font-family: 'Roboto', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${brand.textMuted};
  padding: 0.85rem 1rem;
  border-bottom: 1px solid ${brand.border};
  width: ${(props) => props.$width || 'auto'};
`;

export const Tr = styled.tr`
  transition: background 0.15s ease;

  &:not(:last-child) td {
    border-bottom: 1px solid ${brand.border};
  }

  tbody &:hover {
    background: ${brand.inputBg};
  }
`;

export const Td = styled.td`
  padding: 1rem;
  vertical-align: top;
  font-family: 'Roboto', sans-serif;
  font-size: 0.86rem;
  color: ${brand.textPrimary};
  white-space: normal;
  overflow-wrap: break-word;
`;

export const ExpandToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${brand.textMuted};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${brand.inputBg};
    color: ${brand.blue};
  }
`;

export const ServiceTitle = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 0.92rem;
  color: ${brand.textPrimary};
`;

export const ServiceSubtitle = styled.div`
  font-size: 0.8rem;
  color: ${brand.textMuted};
  margin-top: 2px;
`;

export const LocationCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${brand.textSecondary};
`;

export const UrgencyBadge = styled.span`
  display: inline-block;
  font-family: 'Roboto', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 10px;

  ${(props) => {
    const c = urgencyColors[props.$urgency] || urgencyColors['just-quotes'];
    return `
      background: ${c.bg};
      color: ${c.color};
      border: 1px solid ${c.border};
    `;
  }}
`;

export const StatusBadge = styled.span`
  display: inline-block;
  font-family: 'Roboto', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 10px;

  ${(props) => {
    const c = statusColors[props.$status] || statusColors.pending;
    return `
      background: ${c.bg};
      color: ${c.color};
      border: 1px solid ${c.border};
    `;
  }}
`;

export const StatusActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const StatusActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  border: 1px solid ${(props) => actionTones[props.$tone] || brand.blue};
  border-radius: 8px;
  padding: 5px 12px;
  background: ${(props) => actionToneBg[props.$tone] || 'rgba(13, 78, 254, 0.08)'};
  color: ${(props) => actionTones[props.$tone] || brand.blue};
  cursor: pointer;
  transition: all 0.15s ease;

  svg {
    font-size: 0.95rem;
  }

  &:hover:not(:disabled) {
    background: ${(props) => actionTones[props.$tone] || brand.blue};
    color: ${brand.white};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const NoActionsLabel = styled.span`
  display: block;
  width: 100%;
  text-align: center;
  font-family: 'Roboto', sans-serif;
  font-size: 0.82rem;
  color: ${brand.textMuted};
`;

export const ActionErrorText = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 0.85rem;
  color: ${brand.danger};
  margin: -0.5rem 0 1rem;
`;

export const ContactName = styled.div`
  font-weight: 600;
  color: ${brand.textPrimary};
  overflow-wrap: break-word;
`;

export const ContactDetail = styled.div`
  font-size: 0.8rem;
  color: ${brand.textMuted};
  overflow-wrap: break-word;
`;

export const PreferredContactBadge = styled.span`
  display: block;
  font-size: 0.65rem;
  font-weight: 700;
  color: ${brand.blue};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin: 0.1rem 0 0.2rem;
  white-space: nowrap;
`;

/* ─── Expandable detail row ─── */
export const DetailRow = styled.tr`
  background: ${brand.inputBg};
`;

export const DetailCell = styled(Td)`
  white-space: normal;
  padding: 1.5rem 1.75rem 1.75rem;
`;

export const DetailPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: ${brand.white};
  border: 1px solid ${brand.border};
  border-radius: 12px;
  padding: 1rem 1.25rem;
`;

export const DetailSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Roboto', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${brand.textMuted};

  &::before {
    content: '';
    width: 3px;
    height: 12px;
    border-radius: 2px;
    background: linear-gradient(180deg, ${brand.teal} 0%, ${brand.blue} 100%);
  }
`;

export const DetailNote = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 0.9rem;
  font-style: italic;
  color: ${brand.textPrimary};
  white-space: pre-wrap;
  margin: 0;
  padding: 0.7rem 1rem;
  background: ${brand.inputBg};
  border-left: 3px solid ${brand.teal};
  border-radius: 0 8px 8px 0;
`;

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.6rem;
`;

export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0.55rem 0.7rem;
  background: ${brand.inputBg};
  border-radius: 8px;
`;

export const DetailLabel = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 0.72rem;
  color: ${brand.textMuted};
`;

export const DetailValue = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 0.86rem;
  font-weight: 600;
  color: ${brand.textPrimary};
`;

/* ─── Empty state ─── */
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
  padding: 3rem 1.5rem;
  border: 1px dashed ${brand.border};
  border-radius: 12px;
  color: ${brand.textSecondary};

  p {
    font-family: 'Roboto', sans-serif;
    font-size: 0.92rem;
    margin: 0;
  }
`;
